import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { createBusinessDomain } from "../../api/companyApi";
import {
  fetchIndustryGroups,
  fetchIndustryById,
  fetchIndustriesByParent,
} from "../../api/marketApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

const getLang = () => (window.navigator.language === "ja" ? "ja" : "en");

export default function BusinessDomainCreatePage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState({
    businessDomainName: "",
    purpose: "",
    strategyMapUrl: "",
    strategyMapMiddleUrl: "",
  });

  // Industry cascade state
  const [industryGroups, setIndustryGroups] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [industrySubGroups, setIndustrySubGroups] = useState([]);
  const [selectedIndustryGroupId, setSelectedIndustryGroupId] = useState("");
  const [selectedIndustryId, setSelectedIndustryId] = useState("");
  const [selectedIndustrySubGroupId, setSelectedIndustrySubGroupId] = useState("");
  const [industryNames, setIndustryNames] = useState({
    industryGroupName: "",
    industryName: "",
    industrySubGroupName: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const groups = await fetchIndustryGroups();
        setIndustryGroups(Array.isArray(groups) ? groups : []);
      } catch (e) {
        console.error("Failed to load industry groups:", e);
      }
    })();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleIndustryGroupChange = async (e) => {
    const groupId = e.target.value;
    setSelectedIndustryGroupId(groupId);
    setSelectedIndustryId("");
    setSelectedIndustrySubGroupId("");
    setIndustries([]);
    setIndustrySubGroups([]);
    setIndustryNames({ industryGroupName: "", industryName: "", industrySubGroupName: "" });

    if (!groupId) return;
    try {
      const [groupData, children] = await Promise.all([
        fetchIndustryById(groupId),
        fetchIndustriesByParent(groupId),
      ]);
      const name = getLang() === "ja" ? groupData.japaneseName : groupData.englishName;
      setIndustryNames((prev) => ({ ...prev, industryGroupName: name }));
      setIndustries(Array.isArray(children) ? children : []);
    } catch (e) {
      console.error("Failed to load industries:", e);
    }
  };

  const handleIndustryChange = async (e) => {
    const industryId = e.target.value;
    setSelectedIndustryId(industryId);
    setSelectedIndustrySubGroupId("");
    setIndustrySubGroups([]);
    setIndustryNames((prev) => ({ ...prev, industryName: "", industrySubGroupName: "" }));

    if (!industryId) return;
    try {
      const [industryData, children] = await Promise.all([
        fetchIndustryById(industryId),
        fetchIndustriesByParent(industryId),
      ]);
      const name = getLang() === "ja" ? industryData.japaneseName : industryData.englishName;
      setIndustryNames((prev) => ({ ...prev, industryName: name }));
      setIndustrySubGroups(Array.isArray(children) ? children : []);
    } catch (e) {
      console.error("Failed to load industry sub groups:", e);
    }
  };

  const handleIndustrySubGroupChange = async (e) => {
    const subGroupId = e.target.value;
    setSelectedIndustrySubGroupId(subGroupId);

    if (!subGroupId) {
      setIndustryNames((prev) => ({ ...prev, industrySubGroupName: "" }));
      return;
    }
    try {
      const subGroupData = await fetchIndustryById(subGroupId);
      const name = getLang() === "ja" ? subGroupData.japaneseName : subGroupData.englishName;
      setIndustryNames((prev) => ({ ...prev, industrySubGroupName: name }));
    } catch (e) {
      console.error("Failed to load industry sub group:", e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedIndustryGroupId || !selectedIndustryId) {
      notify.error(t("businessDomain.error.industryRequired"));
      return;
    }
    try {
      const associatedUrls = [];
      if (form.strategyMapUrl) {
        associatedUrls.push({ urlTitle: "business_growth_model", url: form.strategyMapUrl });
      }
      if (form.strategyMapMiddleUrl) {
        associatedUrls.push({ urlTitle: "strategy_map_middle", url: form.strategyMapMiddleUrl });
      }

      const data = {
        companyId: COMPANY_ID,
        businessDomainName: form.businessDomainName,
        purpose: form.purpose,
      };

      // Only include associatedUrls if there are entries
      if (associatedUrls.length > 0) {
        data.associatedUrls = associatedUrls;
      }

      // Industry fields
      if (selectedIndustryGroupId && industryNames.industryGroupName) {
        data.industryGroupId = selectedIndustryGroupId;
        data.industryGroupName = industryNames.industryGroupName;
      }
      if (selectedIndustryId && industryNames.industryName) {
        data.industryId = selectedIndustryId;
        data.industryName = industryNames.industryName;
      }
      if (selectedIndustrySubGroupId && industryNames.industrySubGroupName) {
        data.industrySubGroupId = selectedIndustrySubGroupId;
        data.industrySubGroupName = industryNames.industrySubGroupName;
      }

      const result = await createBusinessDomain(data);
      notify.success(t("message.create.success"));
      // Navigate to the new domain's detail page
      const newId = result?.businessDomainId || result?.id;
      if (newId) {
        navigate(`/design/${newId}`, { replace: true });
      } else {
        navigate("/design", { replace: true });
      }
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("businessDomain.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("businessDomain.hint.define")}
            </Typography>
            <TextField
              fullWidth
              required
              multiline
              label={t("businessDomain.name")}
              name="businessDomainName"
              value={form.businessDomainName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label={t("businessDomain.purpose")}
              name="purpose"
              value={form.purpose}
              onChange={handleChange}
              helperText={t("businessDomain.hint.purpose")}
            />
          </Grid>

          {/* Industry cascade */}
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("businessDomain.hint.industry")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t("businessDomain.industryGroup")}</InputLabel>
              <Select
                value={selectedIndustryGroupId}
                label={t("businessDomain.industryGroup")}
                onChange={handleIndustryGroupChange}
              >
                {industryGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {getLang() === "ja" ? g.japaneseName : g.englishName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t("businessDomain.industry")}</InputLabel>
              <Select
                value={selectedIndustryId}
                label={t("businessDomain.industry")}
                onChange={handleIndustryChange}
                disabled={industries.length === 0}
              >
                {industries.map((ind) => (
                  <MenuItem key={ind.id} value={ind.id}>
                    {getLang() === "ja" ? ind.japaneseName : ind.englishName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t("businessDomain.industrySubGroup")}</InputLabel>
              <Select
                value={selectedIndustrySubGroupId}
                label={t("businessDomain.industrySubGroup")}
                onChange={handleIndustrySubGroupChange}
                disabled={industrySubGroups.length === 0}
              >
                <MenuItem value="">
                  <em>-</em>
                </MenuItem>
                {industrySubGroups.map((sg) => (
                  <MenuItem key={sg.id} value={sg.id}>
                    {getLang() === "ja" ? sg.japaneseName : sg.englishName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              type="url"
              label={t("businessDomain.strategyMapUrl")}
              name="strategyMapUrl"
              value={form.strategyMapUrl}
              onChange={handleChange}
              helperText={t("businessDomain.hint.strategyMapUrl")}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              type="url"
              label={t("businessDomain.strategyMapMiddleUrl")}
              name="strategyMapMiddleUrl"
              value={form.strategyMapMiddleUrl}
              onChange={handleChange}
              helperText={t("businessDomain.hint.strategyMapMiddleUrl")}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">
            {t("common.add")}
          </Button>
          <Button variant="outlined" onClick={() => navigate("/design/business-domains")}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
